import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trophy, Users, Play, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/tournaments");
      setTournaments(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const joinByCode = async () => {
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      const info = await api.get(`/tournaments/invite/${joinCode.trim().toUpperCase()}`);
      await api.post(`/tournaments/${info.data.tournament_id}/join`);
      toast.success(`Joined ${info.data.name}`);
      setJoinCode("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-start justify-between flex-wrap gap-6">
          <div>
            <div className="text-xs text-slate-500 font-mono-data tracking-widest">DASHBOARD</div>
            <h1 className="text-4xl md:text-5xl font-bold mt-2 text-slate-50">Hey {user?.first_name || user?.name?.split(' ')[0] || "friend"}.</h1>
            <p className="text-slate-400 mt-2">
              {user?.role === "super_admin"
                ? "Super-admin view — every tournament across every organizer."
                : user?.role === "organizer" && user?.organizer_status === "pending"
                ? "Your organizer account is waiting for super-admin approval."
                : user?.role === "organizer" && user?.organizer_status === "rejected"
                ? "Your organizer account was rejected. Contact the super admin."
                : user?.role === "organizer"
                ? "Ready to run another tournament?"
                : "Jump into a tournament using an invite code."}
            </p>
          </div>
          {(user?.role === "super_admin" || (user?.role === "organizer" && user?.organizer_status === "approved")) && (
            <Link to="/create" data-testid="dashboard-create">
              <Button className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-12 px-6">
                <Plus className="w-4 h-4 mr-2" /> New Tournament
              </Button>
            </Link>
          )}
        </div>

        {user?.role === "organizer" && user?.organizer_status === "pending" && (
          <div className="mt-6 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6 flex items-start gap-4" data-testid="pending-banner">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-amber-300 text-xl">⏳</div>
            <div>
              <div className="text-amber-300 font-semibold">Pending super-admin approval</div>
              <div className="text-slate-400 text-sm mt-1">You'll be able to create tournaments once your account is approved. You can still join existing tournaments by invite code below.</div>
            </div>
          </div>
        )}
        {user?.role === "organizer" && user?.organizer_status === "rejected" && (
          <div className="mt-6 rounded-2xl border border-rose-500/40 bg-rose-500/5 p-6 flex items-start gap-4" data-testid="rejected-banner">
            <div className="w-10 h-10 rounded-lg bg-rose-500/20 border border-rose-500/40 flex items-center justify-center text-rose-300 text-xl">✕</div>
            <div>
              <div className="text-rose-300 font-semibold">Organizer request rejected</div>
              <div className="text-slate-400 text-sm mt-1">Reach out to the super admin if you think this was a mistake. You can still join tournaments as a participant.</div>
            </div>
          </div>
        )}

        {/* Join by code */}
        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">
            <div className="text-xs text-slate-500 font-mono-data tracking-widest">JOIN VIA CODE</div>
            <div className="mt-4 flex gap-2">
              <Input placeholder="ABCD1234" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} className="bg-slate-950 border-white/10 font-mono-data uppercase" data-testid="join-code-input" />
              <Button onClick={joinByCode} disabled={joining} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="join-code-submit">Join</Button>
            </div>
            <p className="text-xs text-slate-500 mt-3">Ask your organizer for the 8-character invite code.</p>
          </div>
          <StatCard icon={<Trophy className="w-5 h-5" />} label="Total tournaments" value={tournaments.length} />
          <StatCard icon={<Play className="w-5 h-5" />} label="Active now" value={tournaments.filter(t => ["in_progress","championship"].includes(t.status)).length} />
        </div>

        <div className="mt-12">
          <h2 className="text-2xl text-slate-50">Your tournaments</h2>
          {loading ? (
            <div className="text-slate-500 mt-6">Loading…</div>
          ) : tournaments.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-white/10 p-12 text-center">
              <Trophy className="w-10 h-10 text-slate-700 mx-auto" />
              <p className="text-slate-400 mt-4">
                {user?.role === "organizer" ? "Create your first tournament to get started." : "Join a tournament using an invite code."}
              </p>
            </div>
          ) : (
            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tournaments.map(t => (
                <Link key={t.tournament_id} to={`/tournament/${t.tournament_id}`} data-testid={`t-card-${t.tournament_id}`} className="group rounded-2xl border border-white/10 bg-slate-900/60 p-6 hover:border-amber-500/40 transition-colors">
                  <div className="flex items-start justify-between">
                    <StatusBadge status={t.status} />
                    <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-amber-400" />
                  </div>
                  <h3 className="mt-4 text-xl text-slate-50">{t.name}</h3>
                  {t.game_name && <p className="text-xs text-slate-500 mt-1 font-mono-data uppercase tracking-wider">{t.game_name}</p>}
                  <div className="mt-6 grid grid-cols-3 gap-3 font-mono-data">
                    <Stat label="players" value={`${t.participants?.length || 0}/${t.total_players}`} />
                    <Stat label="rounds" value={t.num_rounds} />
                    <Stat label="tables" value={Math.floor(t.total_players / t.players_per_table)} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ icon, label, value }) => (
  <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6 flex items-center justify-between">
    <div>
      <div className="text-xs text-slate-500 font-mono-data tracking-widest">{label.toUpperCase()}</div>
      <div className="text-4xl font-bold text-slate-50 font-mono-data mt-1">{value}</div>
    </div>
    <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center">{icon}</div>
  </div>
);

const Stat = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
    <div className="text-slate-100 mt-1">{value}</div>
  </div>
);

function StatusBadge({ status }) {
  const map = {
    open: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    in_progress: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    championship: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300",
    finished: "border-slate-500/30 bg-slate-500/10 text-slate-300",
    archived: "border-slate-500/30 bg-slate-500/10 text-slate-400",
  };
  return <span className={`text-[10px] font-mono-data tracking-widest px-2 py-1 rounded-md border uppercase ${map[status] || map.open}`}>{status.replace("_", " ")}</span>;
}
